import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Link, Snackbar, Alert, Button } from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import OnboardingDialog, { ONBOARDING_STORAGE_KEY } from './components/OnboardingDialog';
import InstallPrompt from './components/InstallPrompt';
import { usePushNotifications } from './hooks/usePushNotifications';
import { Link as RouterLink } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

// Lazy-load every page so each route is a separate JS chunk.
// The initial bundle only ships Navbar + AuthProvider.
const HomePage = lazy(() => import('./pages/HomePage'));
const TeamsPage = lazy(() => import('./pages/TeamsPage'));
const PlayersPage = lazy(() => import('./pages/PlayersPage'));
const PostsPage = lazy(() => import('./pages/PostsPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const TeamDashboardPage = lazy(() => import('./pages/TeamDashboardPage'));
const TeamDiscoveryPage = lazy(() => import('./pages/TeamDiscoveryPage'));
const ParentPortalPage = lazy(() => import('./pages/ParentPortalPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const TryoutsPage = lazy(() => import('./pages/TryoutsPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const TryoutDetailPage = lazy(() => import('./pages/TryoutDetailPage'));
const TeamDetailPage = lazy(() => import('./pages/TeamDetailPage'));
const PostDetailPage = lazy(() => import('./pages/PostDetailPage'));
const TryoutsLandingPage = lazy(() => import('./pages/TryoutsLandingPage'));
const TryoutScoringPage = lazy(() => import('./pages/TryoutScoringPage'));

const PageLoader: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

/** Scrolls to the top on every route change (SPAs keep scroll position otherwise) */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/** Activates push notifications once the user is logged in */
const PushGate: React.FC = () => {
  const { isAuthenticated } = useAuth();
  usePushNotifications(isAuthenticated);
  return null;
};

/** Shows onboarding dialog once per user after their first login */
const OnboardingGate: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !localStorage.getItem(ONBOARDING_STORAGE_KEY)) {
      // Small delay so the page settles before the dialog appears
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, [isAuthenticated]);

  return <OnboardingDialog open={open} onClose={() => setOpen(false)} />;
};

/** Listens for SW update events and shows a reload banner */
const UpdateBanner: React.FC = () => {
  const [updateRegistration, setUpdateRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      setUpdateRegistration((e as CustomEvent).detail.registration);
    };
    window.addEventListener('swUpdateAvailable', handler);
    return () => window.removeEventListener('swUpdateAvailable', handler);
  }, []);

  const handleUpdate = () => {
    if (updateRegistration?.waiting) {
      updateRegistration.waiting.postMessage('SKIP_WAITING');
    }
    setUpdateRegistration(null);
  };

  return (
    <Snackbar open={!!updateRegistration} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert
        severity="info"
        icon={<SystemUpdateAltIcon />}
        action={
          <Button color="inherit" size="small" onClick={handleUpdate}>
            Update
          </Button>
        }
        sx={{ width: '100%', alignItems: 'center' }}
      >
        A new version of Squadhatch is available.
      </Alert>
    </Snackbar>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Analytics />
          <SpeedInsights />
          <ScrollToTop />
          <Navbar />
          <OnboardingGate />
          <PushGate />
          <InstallPrompt />
          <UpdateBanner />
          <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />

                  {/* Public discovery pages (SEO-indexable — must only call public endpoints) */}
                  <Route path="/tryouts" element={<TryoutsPage />} />
                  <Route path="/tryouts/:id" element={<TryoutDetailPage />} />
                  <Route path="/tryouts/:sport/:city" element={<TryoutsLandingPage />} />
                  <Route path="/teams/:id" element={<TeamDetailPage />} />
                  <Route path="/posts/:id" element={<PostDetailPage />} />

                  {/* Protected routes — redirect to /login if not authenticated */}
                  <Route path="/teams" element={<PrivateRoute><TeamsPage /></PrivateRoute>} />
                  <Route path="/teams/:teamId/dashboard" element={<PrivateRoute><TeamDashboardPage /></PrivateRoute>} />
                  <Route path="/discover" element={<PrivateRoute><TeamDiscoveryPage /></PrivateRoute>} />
                  <Route path="/players" element={<PrivateRoute><PlayersPage /></PrivateRoute>} />
                  <Route path="/posts" element={<PrivateRoute><PostsPage /></PrivateRoute>} />
                  <Route path="/parent-portal" element={<PrivateRoute><ParentPortalPage /></PrivateRoute>} />
                  <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
                  <Route path="/messages" element={<PrivateRoute><MessagesPage /></PrivateRoute>} />
                  <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
                  <Route path="/admin" element={<PrivateRoute><AdminDashboardPage /></PrivateRoute>} />
                  <Route path="/subscription" element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
                  <Route path="/tryouts/:id/score" element={<PrivateRoute><TryoutScoringPage /></PrivateRoute>} />

                  {/* Public legal pages */}
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </Box>

          {/* Footer — dark brand panel with internal links (also SEO-crawlable) */}
          <Box
            component="footer"
            sx={{
              mt: 'auto',
              px: 3,
              py: 4,
              background: 'linear-gradient(115deg, #0E1F15 0%, #1B4332 70%, #234F3B 100%)',
              color: 'rgba(255,255,255,0.85)',
            }}
          >
            <Box
              sx={{
                maxWidth: 'lg',
                mx: 'auto',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 3,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box component="img" src="/favicon.svg" alt="" sx={{ width: 24, height: 24, borderRadius: 1 }} />
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontFamily: '"Barlow Condensed", "Arial Narrow", sans-serif',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontSize: '1.2rem',
                      color: '#FFFFFF',
                    }}
                  >
                    Squadhatch
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                  Where kids find their team — free for families.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography variant="caption" sx={{ color: '#FFB300', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Explore
                  </Typography>
                  <Link component={RouterLink} to="/tryouts" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }} underline="hover">Tryouts</Link>
                  <Link component={RouterLink} to="/players" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }} underline="hover">Players</Link>
                  <Link component={RouterLink} to="/teams" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }} underline="hover">Teams</Link>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography variant="caption" sx={{ color: '#FFB300', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Legal
                  </Typography>
                  <Link component={RouterLink} to="/terms" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }} underline="hover">Terms of Service</Link>
                  <Link component={RouterLink} to="/privacy" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }} underline="hover">Privacy Policy</Link>
                </Box>
              </Box>
            </Box>
            <Typography variant="caption" display="block" align="center" sx={{ mt: 3, color: 'rgba(255,255,255,0.45)' }}>
              © {new Date().getFullYear()} Squadhatch. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;
